import {
  getSourceControllerFindAllQueryKey,
  useSourceControllerFindAll,
  type RefSourceDto,
  type ResponseSourceDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { useResponseData } from '../composables/useResponseData';
import { useAuth } from './authStore';
import { registerLoading } from './loadingStore';
import { usePos } from './posStore';
import { useUser } from './userStore';

export const useSourceStore = defineStore(
  'source',
  () => {
    const { primarySource } = useUser();
    const { isPosMode, posSourceId } = usePos();
    // Gated on the session: the store outlives every route, so an ungated query
    // keeps its observer active on the login page and answers every cache-wide
    // invalidation with a request that can only come back 401.
    const { isLoggedIn } = useAuth({ onLogin: () => refetch(), global: true });
    const {
      data: sourcesResponseData,
      refetch,
      isFetching,
    } = useSourceControllerFindAll({
      query: { queryKey: getSourceControllerFindAllQueryKey(), enabled: isLoggedIn },
    });

    // The store outlives every component, so it registers directly instead of
    // going through `watchLoading` - there is no component unmount to clean up on.
    registerLoading(isFetching);

    const currentSource = ref<RefSourceDto | null>(null);

    const sourceData = useResponseData(sourcesResponseData);

    const sources = computed<ResponseSourceDto[]>(() => sourceData.value || []);

    /** The source a checked-in device is locked to, resolved to a full object. */
    const posSource = computed<RefSourceDto | null>(() =>
      posSourceId.value !== null
        ? (sources.value.find((s) => s.id === posSourceId.value) ?? null)
        : null,
    );

    watch(
      [isPosMode, posSource],
      ([posMode, lockedSource]) => {
        if (posMode && lockedSource && currentSource.value?.id !== lockedSource.id) {
          currentSource.value = lockedSource;
        }
      },
      { immediate: true },
    );

    const resetCurrentSource = () => {
      if (isPosMode.value) currentSource.value = posSource.value;
      else currentSource.value = primarySource.value || null;
    };

    watch(sourceData, (availableSources) => {
      const updatedSource = availableSources?.find((s) => s.id === currentSource.value?.id);

      if (updatedSource) {
        currentSource.value = updatedSource;
      } else if (isPosMode.value) {
        currentSource.value = posSource.value;
      } else if (currentSource.value) {
        currentSource.value = primarySource.value || null;
      }
    });

    return {
      sources,
      currentSource,
      posSource,
      resetCurrentSource,
    };
  },
  {
    persist: {
      pick: ['currentSource'],
    },
  },
);

export const useSource = () => usePiniaRefStore(useSourceStore());
